<script>
  // The pre-connected window. Mounts the link manager for the signed-in user and
  // shows the friend's picture the moment both sides are present. See links.js
  // for the privacy rules; nothing here can start sending on its own.
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { createLinkManager } from "../links.js";
  import { callActive, declineUntil, DECLINE_COOLDOWN_MS, linkOpenWith } from "../callState.js";
  import { getDisplayNames } from "../profiles.js";
  import { playChime } from "../chime.js";

  export let user;
  export let db;
  export let prefs = {};
  export let present = false;
  export let stream = null;

  const dispatch = createEventDispatcher();
  const BANNER_MS = 4000;
  const FIRST_FRAMES_SETTLE_MS = 300;

  let manager = null;
  let links = [];
  let openWith = null;
  let unsubLinks = null;
  let unsubOpen = null;
  let videoElement;
  let friendName = "";
  let names = {};
  let bannerVisible = false;
  let bannerTimer = null;
  let openedAt = 0;
  let shownAt = 0;
  let playing = false;

  $: if (manager) manager.setPresent(present);
  $: current = openWith ? links.find((l) => l.friend === openWith) : null;
  $: remoteStream = current ? current.remoteStream : null;
  $: if (videoElement && videoElement.srcObject !== remoteStream) videoElement.srcObject = remoteStream;
  $: statusLine = links.length
    ? links.map((l) => (names[l.friend] || l.friend.slice(0, 6)) + ": " + (l.state === "ready" ? (l.remotePresent ? "there" : "away") : l.state)).join(" | ")
    : "no friends ready";
  $: dispatch("debugStatus", { property: "Links", value: statusLine });

  async function nameFor(uid) {
    if (!uid || names[uid]) return;
    try {
      const found = await getDisplayNames(db, [uid]);
      names = { ...names, [uid]: found[uid] || "" };
    } catch (e) { /* name stays short */ }
  }
  $: links.forEach((l) => nameFor(l.friend));
  $: friendName = openWith ? names[openWith] || "" : "";

  function onOpenChange(uid) {
    const was = openWith;
    openWith = uid;
    linkOpenWith.set(uid);
    if (uid && !was) {
      openedAt = performance.now();
      shownAt = 0;
      playing = false;
      callActive.set(true);
      playChime(prefs);
      bannerVisible = true;
      clearTimeout(bannerTimer);
      dispatch("debugStatus", { property: "Open time", value: "link opening" });
    } else if (!uid && was) {
      callActive.set(false);
      bannerVisible = false;
      clearTimeout(bannerTimer);
      dispatch("debugStatus", { property: "Open time", value: "", clear: true });
    }
  }

  function onPlaying() {
    if (!openWith || playing) return;
    playing = true;
    setTimeout(() => {
      if (!openWith) return;
      shownAt = performance.now();
      const ms = Math.round(shownAt - openedAt);
      console.log(new Date().toISOString().slice(11, 23) + " link window shown " + ms + " ms after both were seen");
      dispatch("debugStatus", { property: "Open time", value: "link: picture " + (ms / 1000).toFixed(1) + "s" });
      bannerTimer = setTimeout(() => (bannerVisible = false), BANNER_MS);
    }, FIRST_FRAMES_SETTLE_MS);
  }

  function cancelOpening() {
    console.log("Window declined; staying unavailable for " + DECLINE_COOLDOWN_MS / 60000 + " minutes");
    declineUntil.set(Date.now() + DECLINE_COOLDOWN_MS);
    // The detector reports nobody here from now on, which closes the window on
    // both sides through the ordinary presence path.
    if (manager) manager.setPresent(false);
  }

  onMount(() => {
    manager = createLinkManager({ user, db, getStream: () => stream });
    window.PassCallLinks = manager; // console aid: PassCallLinks.stats()
    unsubLinks = manager.links.subscribe((v) => (links = v));
    unsubOpen = manager.openWith.subscribe(onOpenChange);
    manager.setPresent(present);
    manager.start();
  });

  onDestroy(() => {
    if (unsubLinks) unsubLinks();
    if (unsubOpen) unsubOpen();
    if (manager) manager.stop();
    if (openWith) callActive.set(false);
    linkOpenWith.set(null);
    clearTimeout(bannerTimer);
    dispatch("debugStatus", { property: "Links", value: "", clear: true });
  });
</script>

<style>
  /* Below the navigation overlay (z-index 100) so the stats card and the hidden
     corner buttons stay usable while a window is open. */
  .link-window {
    position: fixed;
    inset: 0;
    z-index: 90;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .link-window video {
    height: 100%;
    width: auto;
    max-width: none;
  }
  .banner {
    position: fixed;
    inset: auto 0 2rem 0;
    z-index: 100001;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 0 1rem;
    pointer-events: none;
    font-family: Inter, system-ui, sans-serif;
    color: var(--cream);
  }
  .banner__title {
    margin: 0;
    font-size: clamp(1rem, 2vw, 1.4rem);
    font-family: Fraunces, Georgia, serif;
    padding: .5rem 1rem;
    border-radius: 999px;
    background: rgba(21, 16, 14, .6);
  }
  .banner__cancel {
    border: 1px solid var(--gold);
    background: rgba(21, 16, 14, .6);
    color: var(--gold-hi);
    border-radius: 999px;
    padding: .45rem 1.1rem;
    font: inherit;
    font-size: 1rem;
    cursor: pointer;
    pointer-events: auto;
  }
</style>

{#if openWith}
  <div class="link-window" aria-label="Window with {friendName || 'a friend'}">
    <video bind:this={videoElement} autoplay playsinline on:playing={onPlaying} />
  </div>
  {#if bannerVisible}
    <div class="banner" role="status" aria-live="polite">
      <p class="banner__title">{playing ? "Window open with" : "Opening a window with"} {friendName || "a friend"}</p>
      <button type="button" class="banner__cancel" on:click={cancelOpening}>Cancel</button>
    </div>
  {/if}
{/if}
